import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import * as api from '@codegrade/api';
import { Maybe } from '@codegrade/maybe';
import * as form from '@/components/Form';
import PasswordSuggestions from '@/components/PasswordSuggestions';
import * as utils from '@/utils';
import * as cls from './cls';
import { OTPInput } from './OTPForm';
/** Form for registering a new account. */
export const RegisterAccountDetailsForm = tsx.component({
    name: 'register-account-details-form',
    props: {
        submit: p.ofType().required,
        showPassword: p(Boolean).default(false),
        tenant: p.ofType().default(Maybe.empty),
        email: p.ofType().default(Maybe.empty),
    },
    /** @returns State of the component. */
    data() {
        return {
            code: '',
            username: '',
            name: '',
            password: '',
            repeatPassword: '',
            showOTPHelpText: false,
        };
    },
    computed: {
        /** @returns A validation error if the username has leading or trailing whitespace. */
        usernameValidation() {
            if (this.username.trim() !== this.username) {
                return 'Your username should not start or end with whitespace';
            }
            return undefined;
        },
        /** @returns A validation error if the two password fields do not match. */
        repeatPasswordValidation() {
            if (!this.showPassword) {
                return undefined;
            }
            if (this.repeatPassword.length > 0 && this.password !== this.repeatPassword) {
                return 'Passwords do not match';
            }
            return undefined;
        },
        /** @returns A read-only email display field when the email prop is set, or an empty VNode. */
        emailDisplayNode() {
            return this.email
                .map(email => (<form.Field label="Email" description="This is the email you have entered in the previous step.">
                        <form.Input class={cls.clsReadOnlyInput} readOnly value={email}/>
                    </form.Field>))
                .orDefaultLazy(utils.emptyVNode);
        },
        /** @returns The password and repeat-password fields when `showPassword` is true, or an empty VNode. */
        passwordNode() {
            if (!this.showPassword) {
                return utils.emptyVNode();
            }
            return [
                <form.Field label="Password">
                    <form.Input class={cls.clsInput} type="password" placeholder="Password" autocomplete="new-password" value={this.password} onInput={val => {
                        this.password = val;
                    }} required/>
                </form.Field>,
                <form.Field label="Repeat password" validation={this.repeatPasswordValidation}>
                    <form.Input class={cls.clsInput} type="password" placeholder="Repeat password" autocomplete="new-password" value={this.repeatPassword} onInput={val => {
                        this.repeatPassword = val;
                    }} required/>
                </form.Field>,
            ];
        },
    },
    methods: {
        /**
         * Render error in the form.
         *
         * @param err The error emitted by the form.
         * @returns Rendering of the error.
         */
        renderError(err) {
            if (utils.isApiErrorWithCode(err, 'OBJECT_NOT_FOUND')) {
                // OTP Code is incorrect or the session has expired
                return 'The email verification code is incorrect, or the code has been sent a long time ago, and session has expired. Please verify the email verification code is correct, or restart the registration. ';
            }
            if (utils.isApiErrorWithCode(err, 'WEAK_PASSWORD') &&
                err.error instanceof api.WeakPasswordException) {
                return <PasswordSuggestions error={err.error}/>;
            }
            return utils.getErrorMessage(err);
        },
    },
    /** @returns The rendered registration form JSX. */
    render() {
        return (<div class="@container">
                <form.Root handleSubmit={async () => {
                this.showOTPHelpText = false;
                if (this.usernameValidation != null) {
                    throw new Error(this.usernameValidation);
                }
                if (this.showPassword && this.password !== this.repeatPassword) {
                    throw new Error('Passwords do not match');
                }
                return await this.submit({
                    username: this.username,
                    name: this.name,
                    password: this.password === '' ? undefined : this.password,
                    code: this.code,
                });
            }} submitLoadingMessage="Creating account" submitSuccessMessage="Account created" submitLabel="Register" submitOnEnter actionBarVariant="wide-submit" scopedSlots={{
                formError: this.renderError,
            }} onError={err => {
                if (utils.isApiErrorWithCode(err.error, 'OBJECT_NOT_FOUND')) {
                    this.showOTPHelpText = true;
                }
            }} layout="row" validationOptions={{
                validationErrorMessage: 'Please fill in all the fields to register',
            }}>
                    {this.emailDisplayNode}

                    <form.Field label="Email Verification Code" description={`Enter the email verification code that was sent to ${this.$router
                .getQuery('email')
                .orDefault('your email')}.`} validation={this.showOTPHelpText
                ? 'Please ensure the OTP code is recent and correct'
                : undefined}>
                        <OTPInput autofocus value={this.code} fontSize="text-inherit" onInput={val => {
                this.code = val;
            }} required/>
                    </form.Field>
                    <form.Field label="Username" description="You cannot change your username after the registration." validation={this.usernameValidation}>
                        <form.Input class={cls.clsInput} type="text" autocomplete="username" value={this.username} onInput={val => {
                this.username = val;
            }} required/>
                    </form.Field>

                    <form.Field label="Full Name" description="How we should address you in CodeGrade.">
                        <form.Input class={cls.clsInput} type="text" autocomplete="name" value={this.name} onInput={val => {
                this.name = val;
            }} required/>
                    </form.Field>

                    {this.passwordNode}
                </form.Root>
            </div>);
    },
});
